/* import __COLOCATED_TEMPLATE__ from './video-topic-selector.hbs'; */
import { task } from 'ember-concurrency';
import { cancel, debounce } from '@ember/runloop';
import { observer, computed } from '@ember/object';
import { bool } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { A } from '@ember/array';
import { on } from '@ember/object/evented';
import Component from '@ember/component';

/** @type {Function} */
const noop = () => { };

/**
 * @param {VideoTopic} videoTopic
 * @returns {{ key: String, model: VideoTopic }}
 */
const mapTopicToOption = (videoTopic) => ({
  key: videoTopic.get('label'),
  model: videoTopic,
});

/** @module */
const VideoTopicSelector = Component.extend({
  /** @inheritDoc */
  tagName: '',

  api: service(),
  store: service(),
  shData: service(),

  /** @type {Ember.A} */
  selected: A([]),

  /** @type {String} */
  label: '',

  /** @type {String} */
  _internalValue: '',

  /** @type {Ember.A<VideoTopic>} */
  loadedTopics: A([]),

  /** @type {Ember.A} */
  _selectedTopics: computed('selected.[]', 'loadedTopics.[]', function () {
    const store = this.get('store');
    return this.get('selected')
      .map((id) => store.peekRecord('videoTopic', id))
      .filter((videoTopic) => videoTopic !== null)
      .map(mapTopicToOption);
  }),

  /** @type {Ember.A} */
  _suggestedOptions: A([]),

  /** @type {Boolean} */
  _isLoadingOptions: false,

  /** @type {Boolean} */
  _isLoadingSelections: bool('loadVideoTopic.isRunning'),

  /** @type {debounce|null} The scheduled suggestions update. */
  _scheduledUpdate: null,

  loadVideoTopic: task(function *(id) {
    const videoTopic = yield this.get('store').findRecord('videoTopic', id);
    this.get('loadedTopics').pushObject(videoTopic);
  }),

  /** Loads topics whenever they're not found in the store (i.e. prefilling from a query param). */
  valueObserver: on('init', observer('selected.[]', function () {
    const store = this.get('store');

    this.get('selected').forEach((id) => {
      const videoTopic = store.peekRecord('videoTopic', id);

      if (!videoTopic) {
        this.get('loadVideoTopic').perform(id);
      }
    });
  })),

  /**
   * Fetches topic suggestions based on the provided input.
   * @param {String} query The user input to fetch suggestions for.
   * @returns {Ember.A<VideoTopic>} The suggestions.
   */
  _getSuggestions(query) {
    const endpoint = 'video-topics';
    const options = { params: { label_search: query } };

    return this.get('api').request(endpoint, options)
      .then((response) => {
        this.shData.pushResponseToStore(response);
        const videoTopics = this.shData.getModelArrayFromResponse(response, 'videoTopic');
        this.get('loadedTopics').pushObjects(videoTopics);
        return videoTopics;
      });
  },

  /** Makes the suggestions request and updates the options array. */
  _updateSuggestions() {
    this._getSuggestions(this.get('_internalValue'))
      .then((results) => {
        const options = results
          .filter((result) => !this.get('selected').includes(result.get('id')))
          .map(mapTopicToOption);

        this.set('_suggestedOptions', options);
        this.get('onsuggestionsupdate')?.();
      })
      .finally(() => {
        this.set('_isLoadingOptions', false);
      });
  },

  /** @inheritDoc */
  actions: {
    handleBlur() {
      this.set('_internalValue', '');
      this.set('_suggestedOptions', A([]));
      cancel(this.get('_scheduledUpdate'));
      this.set('_isLoadingOptions', false);
      this.get('onblur')?.();
    },

    /**
     * @param {String} value
     */
    handleInput(value) {
      this.set('_internalValue', value);

      if (value === '' || value === ' ') {
        this.set('_suggestedOptions', A([]));
        cancel(this.get('_scheduledUpdate'));
        this.set('_isLoadingOptions', false);
        return;
      }

      this.set('_isLoadingOptions', true);
      this.set('_scheduledUpdate', debounce(this, this._updateSuggestions, 250));
    },

    /**
     * @param {Object} option
     * @param {VideoTopic} option.model
     */
    handleAdded({ model }) {
      this.set('_internalValue', '');
      this.set('_suggestedOptions', A([]));
      cancel(this.get('_scheduledUpdate'));
      this.set('_isLoadingOptions', false);

      const index = this.get('selected').findIndex((id) => model.get('id') === id);

      if (index === -1) {
        this.get('onadded')?.(model);
      }
    },

    /**
     * @param {Object} option
     * @param {VideoTopic} option.model
     */
    handleRemoved({ model }) {
      this.get('onremoved')?.(model);
    },
  },
});

export default VideoTopicSelector;
